import {
  PINCODE_LENGTH,
  type DeliveryLocation,
  type LocationSuggestion,
} from '../constants/location'

const INDIAN_PINCODE = /\b[1-9]\d{5}\b/g

/** Last 6-digit PIN in a geocoded area label, e.g. "Karnataka 560008, India". */
export function pincodeFromArea(label: string): string {
  const matches = label.match(INDIAN_PINCODE)
  return matches?.[matches.length - 1] ?? ''
}

export function isValidPincode(value: string): boolean {
  return new RegExp(`^[1-9]\\d{${PINCODE_LENGTH - 1}}$`).test(value.trim())
}

export function sanitizePincodeInput(value: string): string {
  return value.replace(/\D/g, '').slice(0, PINCODE_LENGTH)
}

/** Pincode the delivery area resolved to, from the geocoder or its label. */
export function deliveryLocationPincode(location: DeliveryLocation | null | undefined): string {
  if (!location) return ''
  return location.pincode?.trim() || pincodeFromArea(location.label)
}

export function deliveryLocationFromSuggestion(suggestion: LocationSuggestion): DeliveryLocation {
  const pincode = suggestion.pincode?.trim()
  return {
    label: suggestion.label,
    city: suggestion.city,
    ...(pincode ? { pincode } : {}),
    lat: suggestion.lat,
    lng: suggestion.lng,
  }
}

function locationFromName(value: string): DeliveryLocation | null {
  const name = value.trim()
  if (!name) return null
  return { label: name, city: name }
}

export function parseStoredLocation(raw: string | null): DeliveryLocation | null {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed === 'string') {
      return locationFromName(parsed)
    }
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    const record = parsed as {
      label?: unknown
      city?: unknown
      pincode?: unknown
      lat?: unknown
      lng?: unknown
    }
    if (
      typeof record.city === 'string' &&
      record.city.trim() &&
      typeof record.label === 'string' &&
      record.label.trim()
    ) {
      return {
        label: record.label.trim(),
        city: record.city.trim(),
        ...(typeof record.pincode === 'string' && record.pincode.trim()
          ? { pincode: record.pincode.trim() }
          : {}),
        ...(typeof record.lat === 'number' ? { lat: record.lat } : {}),
        ...(typeof record.lng === 'number' ? { lng: record.lng } : {}),
      }
    }
    return null
  } catch {
    return locationFromName(raw)
  }
}

export function serializeLocation(location: DeliveryLocation): string {
  return JSON.stringify(location)
}
